import { hashKey, useQueryClient } from "@tanstack/react-query";
import { clear } from "idb-keyval";
import { type Dispatch, type SetStateAction, useCallback } from "react";
import { type UseQueryOptions, useQuery } from "src/lib";
import { persistedJsonReplacer, persistedJsonReviver } from "src/lib/persistedJson";
import { idbDelSafe, idbGetSafe } from "src/stores/safeIdb";

/** Clear the default idb-keyval store (used on logout). */
export const idbClear = (): Promise<void> => clear();

export interface PersistOptions<TData> {
  /** Custom deserializer — mapped to `select` under the hood. */
  deserialize?: (data: any) => TData;
  /**
   * Controls whether the server query fires when cached data already exists.
   * - `true`: always fetch from the server on mount (staleTime: 0).
   * - `false` / `undefined`: only fetch if data is stale (default 30s).
   */
  initialRefetch?: boolean;
  placeholderData?: TData | (() => TData);
  /**
   * After this many seconds, cached data is considered stale and the server query will fire.
   * Defaults to 30. If set to -1, data is never considered stale.
   */
  rehydrateAfterSeconds?: number;
  /** "fetch-only" prevents this query from being persisted (gcTime: 0). "both" (default) persists normally. */
  behaviour?: "fetch-only" | "both";
  /**
   * @deprecated No longer used — TanStack persist handles persistence by queryKey automatically.
   */
  cacheKey?: readonly unknown[];
  /**
   * @deprecated No longer used — idb-keyval handles serialization via structured clone.
   */
  serialize?: (data: TData) => unknown;
}

export const UI_STATE_KEY = "__ui_state__";
const UI_STATE_STORAGE_PREFIX = "fraction:ui-state:";

function readUiState<T>(key: string): T | undefined {
  try {
    const raw = localStorage.getItem(UI_STATE_STORAGE_PREFIX + key);
    return raw === null ? undefined : (JSON.parse(raw, persistedJsonReviver) as T);
  } catch {
    return undefined;
  }
}

function writeUiState(key: string, value: unknown) {
  try {
    localStorage.setItem(UI_STATE_STORAGE_PREFIX + key, JSON.stringify(value, persistedJsonReplacer));
  } catch {
    /* full or disabled storage — the value still applies for this session */
  }
}

/**
 * UI state used to live in the query persister, which drops every row whenever a build's entities
 * change and after a day unread — so preferences reset on most app updates. Carry over a value it
 * still holds, whatever its buster or age, then drop the row.
 */
async function migrateUiStateFromQueryPersister<T>(key: string): Promise<T | undefined> {
  const rowKey = `tanstack-query-${hashKey([UI_STATE_KEY, key])}`;
  const raw = await idbGetSafe<string>(rowKey);
  if (typeof raw !== "string") return undefined;
  try {
    const data = (JSON.parse(raw, persistedJsonReviver) as { state?: { data?: T } }).state?.data;
    if (data !== undefined) writeUiState(key, data);
    return data;
  } catch {
    return undefined;
  } finally {
    idbDelSafe(rowKey).catch(() => {});
  }
}

/**
 * useState-like hook shared across components through React Query and persisted to localStorage,
 * so it survives reloads, sign-outs and app updates.
 * Returns `[value, setValue, isLoading, isFetching]`.
 */
export function useCachedState<T>(
  defaultValue: T,
  key: string
): [T, Dispatch<SetStateAction<T>>, boolean, boolean] {
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: [UI_STATE_KEY, key],
    initialData: () => readUiState<T>(key),
    queryFn: async () => (await migrateUiStateFromQueryPersister<T>(key)) ?? defaultValue,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    persister: undefined,
  });

  const setValue = useCallback(
    (v: T | ((prev: T) => T)) => {
      const next = qc.setQueryData<T>([UI_STATE_KEY, key], (prev) =>
        typeof v === "function" ? (v as (prev: T) => T)(prev ?? defaultValue) : v
      );
      writeUiState(key, next);
    },
    [qc, key, defaultValue]
  );

  return [data ?? defaultValue, setValue, false, false];
}

/**
 * Maps PersistOptions + UseQueryOptions into plain TanStack Query options.
 * Pure function — safe to call outside of React components/hooks.
 *
 * ## Option mapping
 * - `initialRefetch: true` → `staleTime: 0`, `refetchOnMount: "always"`
 * - `initialRefetch: false` → `staleTime` based on `rehydrateAfterSeconds` (default 30s)
 * - `rehydrateAfterSeconds: -1` → `staleTime: Infinity`
 * - `deserialize` → composed into `select`
 * - `behaviour: "fetch-only"` → `gcTime: 0`
 * - `cacheKey` / `serialize` → ignored (deprecated)
 */
export function mapCachedQueryOptions<
  TQueryFnData = unknown,
  TCachedData = TQueryFnData,
  TData = TQueryFnData,
>(
  options: UseQueryOptions<TQueryFnData, any, TData> & PersistOptions<TCachedData>
): UseQueryOptions<TQueryFnData, any, TData> {
  const staleTime = options.initialRefetch
    ? 0
    : options.rehydrateAfterSeconds === -1
      ? Number.POSITIVE_INFINITY
      : (options.rehydrateAfterSeconds ?? 30) * 1000;

  // Compose deserialize into select if both are provided
  const { deserialize } = options;
  let composedSelect: ((data: TQueryFnData) => TData) | undefined;
  if (deserialize && options.select) {
    const sel = options.select;
    composedSelect = ((data: any) => sel(deserialize(data) as any)) as (data: TQueryFnData) => TData;
  } else if (deserialize) {
    composedSelect = deserialize as unknown as (data: TQueryFnData) => TData;
  }

  // Strip PersistOptions fields before passing to useQuery
  const {
    initialRefetch: _ir,
    rehydrateAfterSeconds: _ras,
    deserialize: _d,
    behaviour: _b,
    cacheKey: _ck,
    serialize: _s,
    ...queryOptions
  } = options;

  if (composedSelect) {
    queryOptions.select = composedSelect;
  }
  queryOptions.staleTime = staleTime;
  // When initialRefetch is true, always fetch on mount — even if data exists from the persister.
  // This matches the old behavior where the server query always fired when initialRefetch was true.
  if (options.initialRefetch) {
    queryOptions.refetchOnMount = "always";
  }
  if (options.behaviour === "fetch-only") {
    queryOptions.gcTime = 0;
  }

  return queryOptions as UseQueryOptions<TQueryFnData, any, TData>;
}

/**
 * A useQuery wrapper that maps the old caching options to standard TanStack Query options.
 * Persistence is handled automatically by PersistQueryClientProvider.
 */
export function useCachedQuery<TQueryFnData = unknown, TCachedData = TQueryFnData, TData = TQueryFnData>(
  options: UseQueryOptions<TQueryFnData, any, TData> & PersistOptions<TCachedData>
) {
  const mapped = mapCachedQueryOptions(options);
  const q = useQuery<TQueryFnData, any, TData>(mapped);

  // Widen `data` to `TData | undefined` to prevent TypeScript from resolving
  // TData as `never` when the UseQueryResult discriminated union is spread.
  const result: typeof q & { data: TData | undefined; isCacheLoading: false } = q as any;
  result.isCacheLoading = false;
  return result;
}
